import React from "react";
import {
  Box,
  IconButton,
  Paper,
  Typography,
  LinearProgress,
  Collapse,
  List,
  ListItem,
  ListItemText,
  Stack,
} from "@mui/material";
import {
  KeyboardArrowDown as CollapseIcon,
  KeyboardArrowUp as ExpandIcon,
  Close as CloseIcon,
  CloudUpload as UploadIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
} from "@mui/icons-material";
import { useDispatch } from "react-redux";
import {
  clearUploadProgress,
  toggleCollapsed,
  clearAllUploads,
  UploadProgressState,
} from "../store/documentUploadsSlice";

interface UploadStatusWindowProps {
  uploads: { [id: string]: UploadProgressState };
  isCollapsed: boolean;
}

export const UploadStatusWindow: React.FC<UploadStatusWindowProps> = ({
  uploads,
  isCollapsed,
}) => {
  const dispatch = useDispatch();
  const uploadsList = Object.values(uploads).sort(
    (a, b) => b.timestamp - a.timestamp
  );

  if (uploadsList.length === 0) {
    return null;
  }

  return (
    <Paper
      elevation={8}
      sx={{
        position: "fixed",
        bottom: 16,
        right: 16,
        width: 360,
        maxHeight: "80vh",
        zIndex: 1000,
        overflow: "hidden",
        boxShadow:
          "0px 4px 12px rgba(0, 0, 0, 0.15), 0px 8px 24px rgba(0, 0, 0, 0.15) !important",
        border: "none !important",
      }}
      data-testid="upload-status-window"
    >
      <Box
        sx={{
          p: 1,
          bgcolor: "primary.main",
          color: "primary.contrastText",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Typography variant="subtitle1">Document Uploads</Typography>
        <Stack direction="row" spacing={1}>
          <IconButton
            size="small"
            onClick={() => dispatch(toggleCollapsed())}
            sx={{ color: "inherit" }}
          >
            {isCollapsed ? <ExpandIcon /> : <CollapseIcon />}
          </IconButton>
          <IconButton
            size="small"
            onClick={() => dispatch(clearAllUploads())}
            sx={{ color: "inherit" }}
            data-testid="close-upload-status"
          >
            <CloseIcon />
          </IconButton>
        </Stack>
      </Box>

      <Collapse in={!isCollapsed}>
        <List sx={{ maxHeight: "60vh", overflow: "auto" }}>
          {uploadsList.map((upload) => (
            <ListItem
              key={upload.id}
              sx={{
                borderBottom: 1,
                borderColor: "divider",
                py: 2,
              }}
              data-testid={`upload-status-item-${upload.fileName}`}
              secondaryAction={
                <IconButton
                  edge="end"
                  aria-label="remove"
                  onClick={() =>
                    dispatch(clearUploadProgress({ id: upload.id }))
                  }
                >
                  <CloseIcon />
                </IconButton>
              }
            >
              <ListItemText
                primary={
                  <Box sx={{ mb: 1 }}>
                    <Typography
                      variant="body2"
                      component="div"
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mb: 0.5,
                      }}
                    >
                      <span
                        style={{
                          maxWidth: "200px",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {upload.fileName}
                      </span>
                      <IconButton
                        size="small"
                        sx={{
                          ml: 1,
                          color: (theme) => {
                            switch (upload.status) {
                              case "uploading":
                                return theme.palette.primary.main;
                              case "success":
                                return theme.palette.success.main;
                              case "error":
                                return theme.palette.error.main;
                              default:
                                return theme.palette.text.secondary;
                            }
                          },
                        }}
                        data-testid={`upload-status-${upload.fileName}`}
                      >
                        {upload.status === "uploading" && <UploadIcon />}
                        {upload.status === "success" && (
                          <SuccessIcon data-testid="CheckCircleIcon" />
                        )}
                        {upload.status === "error" && <ErrorIcon />}
                      </IconButton>
                    </Typography>
                  </Box>
                }
                secondary={
                  <Box sx={{ width: "100%" }}>
                    {upload.status === "uploading" && (
                      <LinearProgress
                        variant="determinate"
                        value={upload.progress}
                        sx={{ height: 4, borderRadius: 2 }}
                        data-testid={`upload-progress-bar-${upload.fileName}`}
                      />
                    )}
                    {upload.status === "error" && (
                      <Typography
                        variant="caption"
                        color="error"
                        sx={{ display: "block", mt: 0.5 }}
                      >
                        {upload.errorMessage}
                      </Typography>
                    )}
                  </Box>
                }
              />
            </ListItem>
          ))}
        </List>
      </Collapse>
    </Paper>
  );
};
